import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

/**
 * Shared branch lookup for the current church. `hasBranches` is the
 * de facto signal for "should branch UI show at all" — the backend doesn't
 * expose a literal flag on the church record, so a non-empty branch list is
 * the only reliable indicator. `canFilterByBranch` additionally excludes
 * branch_admin users, who are hard-locked to their own branch server-side
 * and have no use for a branch picker/filter.
 */
export default function useBranches() {
  const { user, church } = useAuth();
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBranches = useCallback(async () => {
    if (!church?.id) {
      setIsLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/branches/church/${church.id}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setBranches(data.data || data.branches || (Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const hasBranches = branches.length > 0;
  const canFilterByBranch = hasBranches && user?.role !== "branch_admin";

  return { branches, isLoading, hasBranches, canFilterByBranch };
}
